import { useIsAdmin } from '../auth/hooks/useIsAdmin';
import { AnalyzerDish } from 'types';

import {
  Grid,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  IconButton,
  Chip,
} from '@mui/material';
import GreenButton from '../../components/GreenButton';
import { useState } from 'react';
import DeleteConfirmationDialog from '../../components/DeleteConfirmationDialog';
import DishesDialog from './DishesDialog';
import { AddCircleOutline } from '@mui/icons-material';
import PairAnalyzerDishDialog from './PairAnalyzerDishDialog';
import { useDishes } from './api/getDishes';
import { useDeleteDish } from './api/deleteDish';
import { useAnalyzers } from 'features/analyzers/api/getAnalyzers';
import { useSwitches } from 'features/switches/api/getSwitches';

const ManageDishesPage = () => {
  const isAdmin = useIsAdmin();
  const { data: analyzers } = useAnalyzers({});
  const { data: switches } = useSwitches({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pairDialogOpen, setPairDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { data: dishes, isLoading } = useDishes({});
  const deleteDish = useDeleteDish({});
  const [selectedAnalyzerDish, setSelectedAnalyzerDish] = useState<
    undefined | AnalyzerDish
  >(undefined);
  const [selectedDishId, setSelectedDishId] = useState<null | number>(null);

  const handleClose = () => {
    setDialogOpen(false);
    setSelectedDishId(null);
  };
  const pairHandleClose = () => {
    setPairDialogOpen(false);
    setSelectedDishId(null);
  };
  const handleRowClicked = (dishId: number) => {
    setSelectedDishId(dishId);
  };
  const handleAddConnectionClicked = (event: any, dishId: number) => {
    event.stopPropagation();
    setSelectedDishId(dishId);
    setSelectedAnalyzerDish(undefined);
    setPairDialogOpen(true);
  };
  const handleAnalyzerClicked = (analyzerDish: AnalyzerDish) => {
    setSelectedAnalyzerDish(analyzerDish);
    setPairDialogOpen(true);
  };
  const onSubmitDelete = async () => {
    if (!selectedDishId) return;
    await deleteDish.mutateAsync(selectedDishId);
    setSelectedDishId(null);
    setDeleteDialogOpen(false);
  };
  const onCloseDelete = () => {
    setDeleteDialogOpen(false);
  };
  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };
  const handleNew = () => {
    setSelectedDishId(null);
    setDialogOpen(true);
  };
  if (!isAdmin || !dishes || isLoading) return null;

  return (
    <Grid container spacing={2}>
      <Grid container item xs={12} alignItems='center' spacing={1}>
        <Grid item>
          <GreenButton name='New Dish' onClick={handleNew}></GreenButton>
        </Grid>
        <Grid item>
          <Button
            variant='contained'
            color='primary'
            onClick={() => setDialogOpen(true)}
            disabled={!selectedDishId}
          >
            Edit Dish
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant='contained'
            color='error'
            disabled={!selectedDishId}
            onClick={handleDelete}
          >
            Delete Dish
          </Button>
        </Grid>
      </Grid>
      <Grid item container>
        <Grid item component={Paper} xs>
          <List>
            <Grid container justifyContent='space-between'>
              <Grid item>
                <ListSubheader>Dish</ListSubheader>
              </Grid>
              <Grid item>
                <ListSubheader>Linked Instrument Ports</ListSubheader>
              </Grid>
            </Grid>
            {dishes.map((dish) => (
              <ListItem
                button
                key={dish.id}
                selected={selectedDishId === dish.id}
                onClick={() => handleRowClicked(dish.id)}
                secondaryAction={
                  <IconButton
                    onClick={(event) =>
                      handleAddConnectionClicked(event, dish.id)
                    }
                    edge='end'
                    aria-label='new-connection'
                  >
                    <AddCircleOutline />
                  </IconButton>
                }
              >
                <ListItemText>{dish.name}</ListItemText>
                {dish.analyzer_dish?.map((analyzerDish) => (
                  <Chip
                    key={analyzerDish.id}
                    clickable
                    variant='outlined'
                    label={`${
                      analyzerDish.analyzer_id
                        ? analyzers?.find(
                            (analyzer) =>
                              analyzer.id === analyzerDish.analyzer_id
                          )?.name
                        : analyzerDish.switch_id
                        ? switches?.find(
                            (switchItem) =>
                              switchItem.id === analyzerDish.switch_id
                          )?.name
                        : ''
                    } (Port ${analyzerDish.port})`}
                    sx={{ marginLeft: '.2rem' }}
                    onClick={() => handleAnalyzerClicked(analyzerDish)}
                  />
                ))}
              </ListItem>
            ))}
          </List>
        </Grid>
      </Grid>
      <DishesDialog
        open={dialogOpen}
        handleClose={handleClose}
        dish={dishes.find((dish) => dish.id === selectedDishId)}
      />
      <PairAnalyzerDishDialog
        open={pairDialogOpen}
        handleClose={pairHandleClose}
        analyzerDish={selectedAnalyzerDish}
        dish={dishes.find((dish) => dish.id === selectedDishId)}
      />
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        itemToDelete={
          dishes.find((dish) => dish.id === selectedDishId)?.name || ''
        }
        onSubmit={onSubmitDelete}
        onClose={onCloseDelete}
      />
    </Grid>
  );
};

export default ManageDishesPage;
